// ConsoleTraining.tsx
// Main container for the Training console page.
// Owns all state, data fetching, and handlers.
// Rendering is delegated to section components:
//   - TrainingSubmitForm
//   - ProgressCard
//   - TrainingQuestionsQueue
//   - RuntimeReviewTable

import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import ConsoleLayout from '../ConsoleLayout';
import { useAuth } from '../../../context/AuthContext';
import { consoleTrainingApi } from '../../../services/api/console/training.api';
import { consoleTablesApi } from '../../../services/api/console/tables.api';
import { consoleDashboardsApi } from '../../../services/api/console/dashboards.api';
import trainingInstructionsImg from '../../../Assets/training_instructions.png';
import './ConsoleTraining.css';

import {
  ConsoleDashboardSummary,
  ConsoleManagedTable,
  DashboardSqlValidationResult,
  RuntimeQuestionReviewItem,
  TrainingEntityType,
  TrainingProgressItem,
  TrainingQuestionListItem,
} from '../../../types';

import { ApprovalDraft, ReviewDraft } from './types';
import TrainingSubmitForm from './TrainingSubmitForm';
import ProgressCard from './ProgressCard';
import TrainingQuestionsQueue from './TrainingQuestionsQueue';
import RuntimeReviewTable from './RuntimeReviewTable';

const ConsoleTraining: React.FC = () => {
  const { user } = useAuth();
  const requesterEmail = user?.email || user?.userId || '';

  // ── Entity data state ────────────────────────────────────────────────────────
  const [tables, setTables] = useState<ConsoleManagedTable[]>([]);
  const [dashboards, setDashboards] = useState<ConsoleDashboardSummary[]>([]);
  const [entityType, setEntityType] = useState<TrainingEntityType>('table');
  const [selectedTableEntityId, setSelectedTableEntityId] = useState('');
  const [selectedDashboardEntityId, setSelectedDashboardEntityId] = useState('');
  const [progress, setProgress] = useState<TrainingProgressItem[]>([]);

  // ── Submit form state ────────────────────────────────────────────────────────
  const [question, setQuestion] = useState('');
  const [sqlText, setSqlText] = useState('');
  const [sqlSource, setSqlSource] = useState<'manual' | 'generated'>('manual');
  const [entityMappingConfirmed, setEntityMappingConfirmed] = useState(false);
  const [sqlValidated, setSqlValidated] = useState(false);
  const [is100Correct, setIs100Correct] = useState(false);
  const [overrideDuplicate, setOverrideDuplicate] = useState(false);
  const [duplicateMatch, setDuplicateMatch] = useState('');
  const [duplicateScore, setDuplicateScore] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [generatingSql, setGeneratingSql] = useState(false);
  const [generatingQuestion, setGeneratingQuestion] = useState(false);
  const [validatingSql, setValidatingSql] = useState(false);
  const [sqlValidationResult, setSqlValidationResult] = useState<DashboardSqlValidationResult | null>(null);


  // Load Image of Training Instructions
  const [showInstructions, setShowInstructions] = useState(false);

  // ── Training questions queue state ───────────────────────────────────────────
  const [trainingQuestions, setTrainingQuestions] = useState<TrainingQuestionListItem[]>([]);
  const [loadingTrainingQuestions, setLoadingTrainingQuestions] = useState(false);
  const [savingApprovalQuestionId, setSavingApprovalQuestionId] = useState('');
  const [deletingQuestionId, setDeletingQuestionId] = useState('');
  const [approvalDrafts, setApprovalDrafts] = useState<Record<string, ApprovalDraft>>({});

  // ── Runtime review state ─────────────────────────────────────────────────────
  const [runtimeReviews, setRuntimeReviews] = useState<RuntimeQuestionReviewItem[]>([]);
  const [loadingRuntimeReviews, setLoadingRuntimeReviews] = useState(false);
  const [savingReviewId, setSavingReviewId] = useState('');
  const [reviewDrafts, setReviewDrafts] = useState<Record<string, ReviewDraft>>({});

  // ── Derived values ───────────────────────────────────────────────────────────
  const selectedEntityId = entityType === 'table' ? selectedTableEntityId : selectedDashboardEntityId;

  const entityOptions = useMemo(
    () =>
      entityType === 'table'
        ? tables.map((item) => ({ id: String(item.table_id), label: item.snowflakes_table_name }))
        : dashboards.map((item) => ({ id: item.dashboard_id, label: item.name })),
    [dashboards, entityType, tables]
  );

  const expectedEntityOptions = useMemo(
    () => ({
      table: tables.map((item) => ({ id: String(item.table_id), label: item.snowflakes_table_name })),
      dashboard: dashboards.map((item) => ({ id: item.dashboard_id, label: item.name })),
    }),
    [dashboards, tables]
  );

  const myProgressCount = useMemo(() => {
    const me = progress.find((item) => item.user_email.toLowerCase() === requesterEmail.toLowerCase());
    return me?.submitted_questions || 0;
  }, [progress, requesterEmail]);

  // ── Data fetching ────────────────────────────────────────────────────────────
  const loadEntities = useCallback(async () => {
    if (!requesterEmail || !user?.canAccessConsole) {
      return;
    }
    try {
      const [configured, dashboardRows] = await Promise.all([
        consoleTablesApi.getConfiguredTables(requesterEmail),
        consoleDashboardsApi.listDashboards(requesterEmail),
      ]);
      setTables(configured);
      setDashboards(dashboardRows);

      setSelectedTableEntityId((prev) => {
        if (!configured.length) return '';
        if (prev && configured.some((row) => String(row.table_id) === prev)) return prev;
        return String(configured[0].table_id);
      });

      setSelectedDashboardEntityId((prev) => {
        if (!dashboardRows.length) return '';
        if (prev && dashboardRows.some((row) => row.dashboard_id === prev)) return prev;
        return dashboardRows[0].dashboard_id;
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to load entities');
    }
  }, [requesterEmail, user?.canAccessConsole]);

  const loadProgress = useCallback(async (nextEntityType: TrainingEntityType, nextEntityId: string) => {
    if (!requesterEmail) return;
    if (!nextEntityId) {
      setProgress([]);
      return;
    }
    try {
      const response = await consoleTrainingApi.getTrainingProgress(nextEntityType, nextEntityId, requesterEmail);
      setProgress(response.progress || []);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to load training progress');
    }
  }, [requesterEmail]);

  const loadTrainingQuestions = useCallback(async (nextEntityType: TrainingEntityType, nextEntityId: string) => {
    if (!requesterEmail) return;
    if (!nextEntityId) {
      setTrainingQuestions([]);
      setApprovalDrafts({});
      return;
    }
    setLoadingTrainingQuestions(true);
    try {
      const rows = await consoleTrainingApi.listTrainingQuestions({
        requester_email: requesterEmail,
        entity_type: nextEntityType,
        entity_id: nextEntityId,
        limit: 500,
      });
      setTrainingQuestions(rows);
      const draftMap: Record<string, ApprovalDraft> = {};
      rows.forEach((row) => {
        draftMap[row.question_id] = {
          is_approved: row.main_developer_approved ?? true,
          approval_note: row.approval_note || '',
        };
      });
      setApprovalDrafts(draftMap);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to load training questions');
    } finally {
      setLoadingTrainingQuestions(false);
    }
  }, [requesterEmail]);

  const loadRuntimeReviews = useCallback(async () => {
    if (!requesterEmail || !user?.canAccessConsole) return;
    setLoadingRuntimeReviews(true);
    try {
      const rows = await consoleTrainingApi.getRuntimeReviews(requesterEmail, 200);
      setRuntimeReviews(rows);
      const drafts: Record<string, ReviewDraft> = {};
      rows.forEach((row) => {
        const expectedEntityType: TrainingEntityType =
          row.expected_entity_type === 'dashboard' || row.predicted_entity_type === 'dashboard'
            ? 'dashboard'
            : 'table';
        const candidateEntityId = String(row.expected_entity_id || row.predicted_entity_id || '').trim();
        const validEntityIds =
          expectedEntityType === 'dashboard'
            ? dashboards.map((item) => item.dashboard_id)
            : tables.map((item) => String(item.table_id));
        const expectedEntityId =
          candidateEntityId && validEntityIds.includes(candidateEntityId) ? candidateEntityId : validEntityIds[0] || '';
        drafts[row.review_id] = {
          is_answer_correct: !!row.is_answer_correct,
          is_source_correct: !!row.is_source_correct,
          expected_entity_type: expectedEntityType,
          expected_entity_id: expectedEntityId,
          improvement_note: row.improvement_note || '',
        };
      });
      setReviewDrafts(drafts);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to load runtime reviews');
    } finally {
      setLoadingRuntimeReviews(false);
    }
  }, [dashboards, requesterEmail, tables, user?.canAccessConsole]);

  useEffect(() => {
    void loadEntities();
  }, [loadEntities]);

  useEffect(() => {
    void loadProgress(entityType, selectedEntityId);
    void loadTrainingQuestions(entityType, selectedEntityId);
  }, [entityType, loadProgress, loadTrainingQuestions, selectedEntityId]);

  useEffect(() => {
    void loadRuntimeReviews();
  }, [loadRuntimeReviews]);

  // ── Draft update handlers ────────────────────────────────────────────────────
  const updateReviewDraft = (reviewId: string, patch: Partial<ReviewDraft>) => {
    setReviewDrafts((prev) => ({
      ...prev,
      [reviewId]: {
        is_answer_correct: prev[reviewId]?.is_answer_correct ?? false,
        is_source_correct: prev[reviewId]?.is_source_correct ?? false,
        expected_entity_type: prev[reviewId]?.expected_entity_type ?? 'table',
        expected_entity_id: prev[reviewId]?.expected_entity_id ?? '',
        improvement_note: prev[reviewId]?.improvement_note ?? '',
        ...patch,
      },
    }));
  };

  const updateApprovalDraft = (questionId: string, patch: Partial<ApprovalDraft>) => {
    setApprovalDrafts((prev) => ({
      ...prev,
      [questionId]: {
        is_approved: prev[questionId]?.is_approved ?? true,
        approval_note: prev[questionId]?.approval_note ?? '',
        ...patch,
      },
    }));
  };

  // ── SQL handlers ─────────────────────────────────────────────────────────────
  const handleGenerateSql = async () => {
    if (!requesterEmail || !selectedEntityId) {
      toast.error('Select an entity');
      return;
    }
    if (!question.trim()) {
      toast.error('Enter a question first');
      return;
    }
    setGeneratingSql(true);
    try {
      const result = await consoleTrainingApi.generateTrainingSql({
        requester_email: requesterEmail,
        entity_type: entityType,
        entity_id: selectedEntityId,
        question,
      });
      setSqlText(result.generated_sql || '');
      setSqlSource('generated');
      setSqlValidated(false);
      setSqlValidationResult(null);
      toast.success('SQL generated');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to generate SQL');
    } finally {
      setGeneratingSql(false);
    }
  };

  const handleGenerateQuestion = async () => {
    if (!requesterEmail || !selectedEntityId) {
      toast.error('Select an entity');
      return;
    }
    setGeneratingQuestion(true);
    try {
      const result = await consoleTrainingApi.generateTrainingQuestion({
        requester_email: requesterEmail,
        entity_type: entityType,
        entity_id: selectedEntityId,
      });
      setQuestion(result.generated_question || '');
      toast.success('Question generated');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to generate question');
    } finally {
      setGeneratingQuestion(false);
    }
  };

  const handleValidateSql = async () => {
    if (!requesterEmail || !selectedEntityId) {
      toast.error('Select an entity');
      return;
    }
    if (!sqlText.trim()) {
      toast.error('Enter SQL to validate');
      return;
    }
    setValidatingSql(true);
    try {
      const result = await consoleTrainingApi.validateTrainingSql({
        requester_email: requesterEmail,
        entity_type: entityType,
        entity_id: selectedEntityId,
        sql: sqlText,
      });
      setSqlValidationResult(result);
      setSqlValidated(true);
      toast.success('SQL validated');
    } catch (error: any) {
      setSqlValidated(false);
      setSqlValidationResult(null);
      toast.error(error?.response?.data?.detail || 'Failed to validate SQL');
    } finally {
      setValidatingSql(false);
    }
  };

  // ── Submit question handler ──────────────────────────────────────────────────
  const handleSubmitQuestion = async (event: FormEvent) => {
    event.preventDefault();

    if (!requesterEmail || !selectedEntityId) {
      toast.error('Select an entity');
      return;
    }
    if (!question.trim()) {
      toast.error('Question is required');
      return;
    }
    if (!sqlText.trim()) {
      toast.error('SQL is required for training submission');
      return;
    }
    if (!entityMappingConfirmed) {
      toast.error('Confirm that the question is mapped to the correct entity');
      return;
    }
    if (!sqlValidated) {
      toast.error('Validate SQL before submitting');
      return;
    }
    if (!is100Correct) {
      toast.error('Mark the question as 100% correct before submitting');
      return;
    }

    setSubmitting(true);
    try {
      const result = await consoleTrainingApi.submitTrainingQuestion({
        requester_email: requesterEmail,
        entity_type: entityType,
        entity_id: selectedEntityId,
        question,
        sql_text: sqlText,
        sql_source: sqlSource,
        entity_mapping_confirmed: entityMappingConfirmed,
        sql_validated: sqlValidated,
        is_100_correct: is100Correct,
        source: 'manual_analyst_console',
        override_duplicate: overrideDuplicate,
        approved_by_email: requesterEmail,
      });

      if (result.status === 'duplicate_detected') {
        setDuplicateMatch(result.matched_question || '');
        setDuplicateScore(result.similarity_score || null);
        toast.warn('Similar question detected. Enable override to accept this question.');
      } else {
        setQuestion('');
        setSqlText('');
        setSqlSource('manual');
        setEntityMappingConfirmed(false);
        setSqlValidated(false);
        setIs100Correct(false);
        setOverrideDuplicate(false);
        setDuplicateMatch('');
        setDuplicateScore(null);
        setSqlValidationResult(null);
        toast.success(
          result.approval_required
            ? `Question accepted and pending main developer approval (${result.main_developer_email || 'unassigned'})`
            : `Question accepted. Progress: ${result.progress_count}/30`
        );
        await loadProgress(entityType, selectedEntityId);
        await loadTrainingQuestions(entityType, selectedEntityId);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to submit question');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Runtime review save handler ──────────────────────────────────────────────
  const saveRuntimeReview = async (reviewId: string) => {
    const draft = reviewDrafts[reviewId];
    if (!draft || !draft.expected_entity_id) {
      toast.error('Expected entity is required');
      return;
    }
    setSavingReviewId(reviewId);
    try {
      await consoleTrainingApi.updateRuntimeReview({
        requester_email: requesterEmail,
        review_id: reviewId,
        is_answer_correct: draft.is_answer_correct,
        is_source_correct: draft.is_source_correct,
        expected_entity_type: draft.expected_entity_type,
        expected_entity_id: draft.expected_entity_id,
        improvement_note: draft.improvement_note,
      });
      toast.success('Runtime review updated');
      await loadRuntimeReviews();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to update runtime review');
    } finally {
      setSavingReviewId('');
    }
  };

  // ── Training question delete handler ────────────────────────────────────────
  const handleDeleteTrainingQuestion = async (questionId: string) => {
    if (!window.confirm('Delete this training question? This cannot be undone.')) return;
    setDeletingQuestionId(questionId);
    try {
      await consoleTrainingApi.deleteTrainingQuestion(questionId, requesterEmail);
      toast.success('Question deleted');
      await loadTrainingQuestions(entityType, selectedEntityId);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to delete question');
    } finally {
      setDeletingQuestionId('');
    }
  };

  // ── Training question approval save handler ──────────────────────────────────
  const saveTrainingQuestionApproval = async (questionId: string) => {
    const draft = approvalDrafts[questionId];
    if (!draft || !requesterEmail) return;
    setSavingApprovalQuestionId(questionId);
    try {
      await consoleTrainingApi.approveTrainingQuestionByMainDeveloper({
        requester_email: requesterEmail,
        question_id: questionId,
        is_approved: draft.is_approved,
        approval_note: draft.approval_note,
      });
      toast.success('Main developer approval saved');
      await loadTrainingQuestions(entityType, selectedEntityId);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to save approval');
    } finally {
      setSavingApprovalQuestionId('');
    }
  };

  // ── Access guard ─────────────────────────────────────────────────────────────
  if (!user?.canAccessConsole) {
    return (
      <ConsoleLayout title="Training">
        <div className="console-card">
          <p>You do not have Console access.</p>
        </div>
      </ConsoleLayout>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <ConsoleLayout title="Training">
      <div className="console-grid two-col">
        <TrainingSubmitForm
          entityType={entityType}
          setEntityType={setEntityType}
          selectedEntityId={selectedEntityId}
          setSelectedTableEntityId={setSelectedTableEntityId}
          setSelectedDashboardEntityId={setSelectedDashboardEntityId}
          entityOptions={entityOptions}
          question={question}
          setQuestion={setQuestion}
          sqlText={sqlText}
          setSqlText={setSqlText}
          setSqlSource={setSqlSource}
          setSqlValidated={setSqlValidated}
          setSqlValidationResult={setSqlValidationResult}
          sqlSource={sqlSource}
          sqlValidated={sqlValidated}
          sqlValidationResult={sqlValidationResult}
          generatingSql={generatingSql}
          generatingQuestion={generatingQuestion}
          validatingSql={validatingSql}
          handleGenerateSql={handleGenerateSql}
          handleGenerateQuestion={handleGenerateQuestion}
          handleValidateSql={handleValidateSql}
          overrideDuplicate={overrideDuplicate}
          setOverrideDuplicate={setOverrideDuplicate}
          entityMappingConfirmed={entityMappingConfirmed}
          setEntityMappingConfirmed={setEntityMappingConfirmed}
          is100Correct={is100Correct}
          setIs100Correct={setIs100Correct}
          duplicateMatch={duplicateMatch}
          duplicateScore={duplicateScore}
          submitting={submitting}
          myProgressCount={myProgressCount}
          handleSubmitQuestion={handleSubmitQuestion}
        />

        <ProgressCard progress={progress} />
      </div>

      <TrainingQuestionsQueue
        trainingQuestions={trainingQuestions}
        loadingTrainingQuestions={loadingTrainingQuestions}
        entityType={entityType}
        selectedEntityId={selectedEntityId}
        requesterEmail={requesterEmail}
        approvalDrafts={approvalDrafts}
        savingApprovalQuestionId={savingApprovalQuestionId}
        deletingQuestionId={deletingQuestionId}
        updateApprovalDraft={updateApprovalDraft}
        saveTrainingQuestionApproval={saveTrainingQuestionApproval}
        handleDeleteTrainingQuestion={handleDeleteTrainingQuestion}
        loadTrainingQuestions={loadTrainingQuestions}
      />

      <RuntimeReviewTable
        runtimeReviews={runtimeReviews}
        loadingRuntimeReviews={loadingRuntimeReviews}
        reviewDrafts={reviewDrafts}
        savingReviewId={savingReviewId}
        expectedEntityOptions={expectedEntityOptions}
        updateReviewDraft={updateReviewDraft}
        saveRuntimeReview={saveRuntimeReview}
        loadRuntimeReviews={loadRuntimeReviews}
      />
{/* ── Floating instructions button ── */}
      <button
        type="button"
        className="ct-instructions-btn"
        onClick={() => setShowInstructions(true)}
        title="View instructions"
      >
        ⓘ
      </button>

      {showInstructions ? (
        <div className="ct-instructions-overlay" onClick={() => setShowInstructions(false)}>
          <div className="ct-instructions-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="ct-instructions-close"
              onClick={() => setShowInstructions(false)}
            >
              ×
            </button>
            <img
              src={trainingInstructionsImg}
              alt="Training Instructions"
              className="ct-instructions-img"
            />
          </div>
        </div>
      ) : null}
    </ConsoleLayout>
  );
};

export default ConsoleTraining;